import React, { useState, useEffect, useCallback } from 'react';
import collegeConfig from '../../config/collegeConfig';
import { FaTimes, FaSearch, FaCheckCircle, FaExclamationCircle, FaCheckDouble } from 'react-icons/fa';
import StudentHeader from './StudentHeader';
import Pagination, { ITEMS_PER_PAGE } from './Pagination';
import BulkVerifyModal from './BulkVerifyModal';
import Toast from './Toast';
import useToast from './useToast';

const REVIEW_STATUS_LABEL = {
  pending: 'Pending Review',
  approved: 'Approved',
  needs_revision: 'Needs Revision'
};

// Shared review panel for the 6 "simple log" modules (Reflections,
// Electives, ECE, FAP visits, Conference, Internship activities) — all
// share the same review sub-document shape (approved/needs_revision with
// feedback). Not subject-scoped (see server/routes/ugAetcomRoutes.js's /all
// route comment) — any teacher with teachesUG sees every UG student's
// entries.
//
// Each module's fields differ too much for one generic card layout, so the
// caller supplies renderBody for the full, always-visible entry content.
const SimpleLogReview = ({
  title,
  apiPath,
  listKey,
  getStudent,
  renderBody,
  ugBatches
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reviewStatusFilter, setReviewStatusFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [reviewEntry, setReviewEntry] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [feedbackText, setFeedbackText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const { toast, showSuccess, showError } = useToast();

  const token = localStorage.getItem('token');
  const authHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }), [token]);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/all`, {
        headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) setItems(data[listKey] || []);
    } catch (err) {
      console.error(`Error fetching UG ${apiPath}:`, err);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, apiPath, listKey]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const openReview = (entry) => {
    setReviewEntry(entry);
    setReviewStatus('');
    setFeedbackText(entry.review?.feedback || '');
  };

  const submitReview = async () => {
    if (!reviewStatus) {
      alert('Please choose Approve or Needs Revision.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await fetch(
        `${collegeConfig.api.baseURL}/ug/${apiPath}/${reviewEntry._id}/review`,
        {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify({ status: reviewStatus, feedback: feedbackText })
        }
      );
      const data = await res.json();
      if (data.success) {
        setReviewEntry(null);
        await fetchItems();
        showSuccess(reviewStatus === 'approved' ? 'Entry approved successfully' : 'Entry sent back for revision');
      } else {
        showError(data.message || 'Failed to submit review');
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      showError('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const searchLower = searchTerm.trim().toLowerCase();
  const filtered = items.filter(item => {
    const itemReviewStatus = item.review?.status || 'pending';
    if (reviewStatusFilter && itemReviewStatus !== reviewStatusFilter) return false;
    const student = getStudent(item);
    if (batchFilter) {
      const studentBatchId = student?.profile?.ugBatchId?._id || student?.profile?.ugBatchId;
      if (!studentBatchId || studentBatchId.toString() !== batchFilter) return false;
    }
    if (!searchLower) return true;
    const name = `${student?.profile?.firstName || ''} ${student?.profile?.lastName || ''}`.toLowerCase();
    return name.includes(searchLower);
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [reviewStatusFilter, batchFilter, searchTerm]);

  const pageItems = filtered.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const bulkCandidates = filtered.filter(item => (item.review?.status || 'pending') !== 'approved');

  return (
    <div className="ugtr-root">
      <Toast toast={toast} />
      <div className="ugtr-header">
        <div>
          <div className="ugtr-header-title">{title}</div>
          <div className="ugtr-header-sub">{filtered.length} entries</div>
        </div>
        <button
          className="ugtr-bulk-btn"
          onClick={() => setShowBulkModal(true)}
          disabled={bulkCandidates.length === 0}
        >
          <FaCheckDouble /> Bulk Verify
        </button>
      </div>

      <div className="ugtr-filters">
        <div className="ugtr-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Search by student name..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select value={reviewStatusFilter} onChange={e => setReviewStatusFilter(e.target.value)}>
          <option value="">All entries</option>
          <option value="pending">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="needs_revision">Needs Revision</option>
        </select>
        <select value={batchFilter} onChange={e => setBatchFilter(e.target.value)}>
          <option value="">All batches</option>
          {(ugBatches || []).map(b => (
            <option key={b._id} value={b._id}>{b.batchLabel}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="ugtr-empty">Loading entries...</div>
      ) : filtered.length === 0 ? (
        <div className="ugtr-empty">No entries match these filters.</div>
      ) : (
        <>
          <div className="ugtr-list">
            {pageItems.map(item => {
              const student = getStudent(item);
              return (
                <div key={item._id} className="ugtr-entry-card">
                  <StudentHeader
                    student={student}
                    ugBatches={ugBatches}
                    statusBadge={(
                      <span className={`ugtr-status-badge ugtr-status-${item.review?.status || 'pending'}`}>
                        {REVIEW_STATUS_LABEL[item.review?.status] || REVIEW_STATUS_LABEL.pending}
                      </span>
                    )}
                  />

                  <div className="ugtr-entry-body">
                    {renderBody(item)}
                    {item.review?.feedback && (
                      <div className="ugtr-info-item ugtr-entry-body-full">
                        <span className="ugtr-info-label">Previous Feedback ({item.review.reviewerName})</span>
                        <span className="ugtr-info-value">{item.review.feedback}</span>
                      </div>
                    )}
                  </div>

                  <div className="ugtr-entry-actions">
                    <button className="ugtr-review-btn" onClick={() => openReview(item)}>
                      Review Entry
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <Pagination currentPage={currentPage} totalCount={filtered.length} onPageChange={setCurrentPage} />
        </>
      )}

      {reviewEntry && (
        <div className="ugtr-modal-overlay" onClick={() => !submitting && setReviewEntry(null)}>
          <div className="ugtr-modal" onClick={e => e.stopPropagation()}>
            <div className="ugtr-modal-head">
              <h3>Review entry</h3>
              <FaTimes onClick={() => !submitting && setReviewEntry(null)} />
            </div>

            <div className="ugtr-decision-row">
              <button
                className={`ugtr-decision-btn ugtr-approve ${reviewStatus === 'approved' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('approved')}
              >
                <FaCheckCircle /> Approve
              </button>
              <button
                className={`ugtr-decision-btn ugtr-revision ${reviewStatus === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                onClick={() => setReviewStatus('needs_revision')}
              >
                <FaExclamationCircle /> Needs Revision
              </button>
            </div>

            <label className="ugtr-form-label">Feedback</label>
            <textarea
              className="ugtr-form-textarea"
              value={feedbackText}
              onChange={e => setFeedbackText(e.target.value)}
              placeholder="Notes for the student..."
            />

            <div className="ugtr-modal-actions">
              <button className="ugtr-btn-secondary" onClick={() => setReviewEntry(null)} disabled={submitting}>Cancel</button>
              <button className="ugtr-btn-primary" onClick={submitReview} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit review'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkModal && (
        <BulkVerifyModal
          title={`Bulk Verify ${title}`}
          items={bulkCandidates}
          getId={item => item._id}
          renderItemLabel={item => {
            const student = getStudent(item);
            return (
              <>
                <strong>{student?.profile?.firstName} {student?.profile?.lastName}</strong>
                <span className="ugtr-bulk-item-meta">
                  {REVIEW_STATUS_LABEL[item.review?.status] || REVIEW_STATUS_LABEL.pending}
                </span>
              </>
            );
          }}
          renderForm={(formState, setFormState) => (
            <>
              <div className="ugtr-decision-row">
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-approve ${formState.status === 'approved' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'approved' })}
                >
                  <FaCheckCircle /> Approve
                </button>
                <button
                  type="button"
                  className={`ugtr-decision-btn ugtr-revision ${formState.status === 'needs_revision' ? 'ugtr-decision-active' : ''}`}
                  onClick={() => setFormState({ ...formState, status: 'needs_revision' })}
                >
                  <FaExclamationCircle /> Needs Revision
                </button>
              </div>
              <label className="ugtr-form-label">Feedback</label>
              <textarea
                className="ugtr-form-textarea"
                value={formState.feedback || ''}
                onChange={e => setFormState({ ...formState, feedback: e.target.value })}
                placeholder="Notes for the students..."
              />
            </>
          )}
          isValid={formState => !!formState.status}
          onConfirm={async (selectedItems, formState) => {
            const results = await Promise.allSettled(
              selectedItems.map(item => fetch(`${collegeConfig.api.baseURL}/ug/${apiPath}/${item._id}/review`, {
                method: 'PUT',
                headers: authHeaders(),
                body: JSON.stringify({ status: formState.status, feedback: formState.feedback })
              }))
            );
            const successCount = results.filter(r => r.status === 'fulfilled' && r.value.ok).length;
            setShowBulkModal(false);
            await fetchItems();
            if (successCount === selectedItems.length) {
              showSuccess(`Successfully verified ${successCount} entries`);
            } else {
              showError(`Verified ${successCount} of ${selectedItems.length} entries. Some failed — please retry those.`);
            }
          }}
          onClose={() => setShowBulkModal(false)}
        />
      )}
    </div>
  );
};

export default SimpleLogReview;
